import "./page-actions.scss";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { autoinject, bindable, bindingMode, customElement, Disposable } from "aurelia-framework";
import { NavigationInstruction, PipelineResult, RouteConfig, RouterEvent } from "aurelia-router";
import { isRouteAllowed } from "../../../common/utilities/is-route-allowed";
import { CreateRoutes } from "../../../pages/create/create-routes";
import { ListRoutes } from "../../../pages/list/list-routes";
import { PermissionEvent, PermissionManager } from "../../../common/utilities/permission-manager";
import { AccountsService } from "../../../services/accounts-service";
import { AccountEvent } from "../../../services/auth-service";
import { EnumsService } from "../../../services/enums-service";
import { UsersService } from "../../../services/users-service";
import { CurrentUserService } from "../../../services/current-user-service";
import { EnumMap } from "../../../common/utilities/enum-map";
import { AdministrativePermissionEnum } from "../../../enums/administrative-permission-enum";
import { PermissionActionEnum } from "../../../enums/permission-action-enum";

@autoinject()
@customElement("page-actions")
export class PageActions {
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public isSearchExpanded: boolean;
    private _enumsService: EnumsService;
    private _usersService: UsersService;
    private _currentUserService: CurrentUserService;
    private readonly _accountsService: AccountsService;
    private _ea: EventAggregator;
    private _routeSubscription: Disposable;
    private _permissionManager: PermissionManager;
    private _subscriptions: Subscription[] = [];
    public isListPageActive: boolean = false;
    public isCreatePageActive: boolean = false;
    public createRoutes: RouteConfig[] = [];
    public listRoutes: RouteConfig[] = [];
    public hasOneOrMoreAddPermissions: boolean = true;
    public isPalliativeCare: boolean = false;
    public medAlertsCredentials: string[] = ["RN", "LPN", "LVN", "NP"];
    public showMedAlerts: boolean = false;
    public hasAskAxxessViewPermission: boolean = false;

    public constructor(
        enumsService: EnumsService,
        usersService: UsersService,
        currentUserService: CurrentUserService,
        ea: EventAggregator,
        permissionManager: PermissionManager,
        accountService: AccountsService
    ) {
        this._enumsService = enumsService;
        this._usersService = usersService;
        this._currentUserService = currentUserService;
        this._ea = ea;
        this._permissionManager = permissionManager;
        this._accountsService = accountService;
    }

    private initSubscriptions() {
        // handle provider change and permission refresh
        [AccountEvent.Selected, PermissionEvent.UserPermissionLoaded].forEach((event) => {
            this._subscriptions.push(
                this._ea.subscribe(event, async () => {
                    this.initPermissions();
                    await this.initializeLists();
                })
            );
        });
    }

    private async initializeLists() {
        this.isPalliativeCare = await this._accountsService.getIsPalliativeAccount();
        this.createRoutes = CreateRoutes.filter((route) => this.isLinkAllowed(route) && this.hasPermission(route));
        if (!this.createRoutes.length) {
            this.hasOneOrMoreAddPermissions = false;
        }
        this.listRoutes = ListRoutes.filter((route) => this.isLinkAllowed(route) && this.hasPermission(route));
    }

    public async attached() {
        this.initPermissions();
        await this.initializeLists();
        this._routeSubscription = this._ea.subscribe(
            RouterEvent.Complete,
            (event: { instruction: NavigationInstruction; result: PipelineResult }) => {
                let currentPageUrl = event.instruction.fragment.toLocaleLowerCase();
                this.isListPageActive = !!currentPageUrl && currentPageUrl.includes("/list/");
                this.isCreatePageActive = !!currentPageUrl && currentPageUrl.includes("/create/");
            }
        );
        this.initSubscriptions();
        let currentUser = await this._currentUserService.getUser();
        if (currentUser?.id) {
            let user = await this._usersService.getUser(currentUser.id);
            if (!!user?.credentials?.length) {
                let credentialsEnum = await this._enumsService.getCredentials();
                let credentialsEnumMap = new EnumMap(credentialsEnum);
                let userCredentials = user.credentials;
                this.showMedAlerts = userCredentials.some((credential) => {
                    let credentialValue = credentialsEnumMap.getEnumName(credential) as string;
                    return this.medAlertsCredentials.includes(credentialValue);
                });
            }
        }
    }

    private initPermissions() {
        this.hasAskAxxessViewPermission = this._permissionManager.checkPermission([
            { resource: AdministrativePermissionEnum.AskAxxess, action: PermissionActionEnum.View }
        ]);
    }

    public isLinkAllowed(routeConfig: RouteConfig) {
        let isPalliativeRoute = routeConfig?.settings?.isPalliativeCare;
        // checks for isPalliativeCare flag and adds/removes navigation buttons accordingly
        if (
            (isPalliativeRoute === false && this.isPalliativeCare) ||
            (isPalliativeRoute === true && !this.isPalliativeCare)
        ) {
            return false;
        }
        if (routeConfig) {
            return isRouteAllowed(routeConfig);
        } else {
            return false;
        }
    }

    public hasPermission(route: RouteConfig) {
        return route?.settings?.permission ? this._permissionManager.checkRoutePermission(route) : true;
    }

    public detached() {
        this._routeSubscription?.dispose();
        if (this._subscriptions.length > 0) {
            this._subscriptions.forEach((sub) => sub.dispose());
        }
    }
}
